import { put, call, takeLatest } from 'redux-saga/effects';
import axiosConfig from '../../services/axiosConfig';
import {
  addMaterialFailure,
  addMaterialSuccess,
  fetchMaterialsSuccess,
  fetchMaterialsFailure,
  updateMaterialSuccess,
  updateMaterialFailure,
  bulkUploadMaterialsSuccess,
  bulkUploadMaterialsFailure,
  shareMaterialSuccess,
  shareMaterialFailure,
} from './action';
import {
  ADD_MATERIAL,
  FETCH_MATERIALS,
  UPDATE_MATERIAL,
  BULK_UPLOAD_MATERIALS,
  SHARE_MATERIAL,
} from './types';
import get from 'lodash/get';
import { notification } from 'antd';
import axios from 'axios';

/** Get all Materials */
function* fetchMaterials(payload = {}) {
  try {
    const url = `/materials-module`;
    const res = yield axiosConfig.get(url);

    // Extract data from response structure: { count, rows: [...] }
    const responseData = get(res, ['data', 'data'], {});
    const materialsRows = get(responseData, 'rows', []);
    const count = get(responseData, 'count', 0);
    
    if (res.status === 200) {
      yield put(fetchMaterialsSuccess({ response: materialsRows, count }));
    } else {
      yield put(fetchMaterialsFailure());
      notification.error({
        message: 'Something went wrong. Please try again.',
      });
    }
  } catch (error) {
    yield put(fetchMaterialsFailure());
    notification.destroy();
    notification.error({
      message: 'A problem occurred while fetching the Materials Data.',
      description: `${error}`,
    });
  }
}

/** Function to Add new material */
function* addMaterial(payload) {
  try {
    const materialData = payload.data;
    
    // Format the payload according to API requirements
    const formattedPayload = {
      materialCategory: materialData.MaterialCategory || '',
      name: materialData.Name || '',
      productCode: materialData.ProductCode || '',
      datasheet: materialData.Datasheet || '',
      manufactureName: materialData.ManufactureName || '',
      tradeName: materialData.TradeName || '',
      applications: materialData.Applications || '',
      reference: materialData.Reference || '',
      revisionDate: materialData.RevisionDate || null,
      formerDate: materialData.FormerDate || null,
      versionOrRevisionNumber: materialData.versionOrRevisionNumber || '',
      storageTemperature: materialData.StorageTemperature || '',
      comments: materialData.Comments || '',
      availabilityStatus: materialData.AvailabilityStatus || '',
    };

    const res = yield axiosConfig.post(`/materials-module`, formattedPayload);
    const response = get(res, ['data', 'data'], {});

    notification.destroy();
    if (res.status === 200 || res.status === 201) {
      yield fetchMaterials();
      notification.success({
        message: 'Material Added Successfully.',
      });
      yield put(addMaterialSuccess({ response }));
    } else {
      yield put(addMaterialFailure());
      notification.error({
        message: 'Something went wrong.',
      });
    }
  } catch (error) {
    yield put(addMaterialFailure());
    notification.destroy();
    notification.error({
      message: 'A problem occurred while adding the Material.',
      description: get(error, ['response', 'data', 'message'], error.message),
    });
  }
}

/** Function to Add new material with file upload */
export const OnAddMaterial = async (payload) => {
  try {
    const materialData = payload.data;
    let fileKey = null;
    let fileName = null;
    let fileType = null;
    
    // Check if file is present (file is passed as dataSheet array, similar to certificate in calibration)
    if (materialData.dataSheet && materialData.dataSheet.length > 0) {
      const file = materialData.dataSheet[0];
      
      // Get presigned URL from backend
      const resFile = await axiosConfig.get(
        `/materials-module/get-pre-signed-url?fileName=${file.name}&contentType=${file.type}`
      );
      const responseFile = get(resFile, ['data', 'data'], {});

      if (responseFile && responseFile.presignedUrl) {
        // Upload file directly to S3 using presigned URL
        const presignedResponse = await axios.put(
          responseFile.presignedUrl,
          file
        );

        if (presignedResponse.status === 200) {
          // Store fileKey, fileName, and fileType from S3 response
          fileKey = responseFile.fileKey;
          fileName = responseFile.fileName || file.name;
          fileType = file.type || null;
        } else {
          notification.error({ message: 'File Uploading Failed. Please try again.' });
          return false;
        }
      } else {
        notification.error({
          message: 'Something went wrong while getting presigned URL.',
        });
        return false;
      }
    }

    // Format the payload according to API requirements
    const formattedPayload = {
      materialCategory: materialData.MaterialCategory || '',
      name: materialData.Name || '',
      productCode: materialData.ProductCode || '',
      datasheet: materialData.Datasheet || '',
      manufactureName: materialData.ManufactureName || '',
      tradeName: materialData.TradeName || '',
      applications: materialData.Applications || '',
      reference: materialData.Reference || '',
      revisionDate: materialData.RevisionDate || null,
      formerDate: materialData.FormerDate || null,
      versionOrRevisionNumber: materialData.versionOrRevisionNumber || '',
      storageTemperature: materialData.StorageTemperature || '',
      comments: materialData.Comments || '',
      availabilityStatus: materialData.AvailabilityStatus || '',
      ...(fileKey && { fileKey: fileKey }), // fileKey from S3
      ...(fileName && { fileName: fileName }),
      ...(fileType && { fileType: fileType }), // fileType from file
    };

    const res = await axiosConfig.post(`/materials-module`, formattedPayload);
    const response = get(res, ['data', 'data'], {});

    notification.destroy();
    if (res.status === 200 || res.status === 201) {
      notification.success({
        message: 'Material Added Successfully.',
      });
      return false; // Return false to indicate success (following calibration pattern)
    } else {
      notification.error({
        message: 'Something went wrong.',
      });
      return false;
    }
  } catch (error) {
    console.log(error);
    notification.destroy();
    notification.error({
      message: 'A problem occurred while adding the Material.',
      description: get(error, ['response', 'data', 'message'], error.message || error),
    });
    return false;
  }
};

/** Function to Update material */
function* updateMaterial(payload) {
  try {
    const materialData = payload.data;
    const materialId = payload.id;
    
    // Format the payload according to API requirements with materialsModule wrapper
    const formattedPayload = {
        materialsModuleId: materialId,
        materialCategory: materialData.MaterialCategory || '',
        name: materialData.Name || '',
        productCode: materialData.ProductCode || '',
        datasheet: materialData.Datasheet || '',
        manufactureName: materialData.ManufactureName || '',
        tradeName: materialData.TradeName || '',
        applications: materialData.Applications || '',
        reference: materialData.Reference || '',
        revisionDate: materialData.RevisionDate || null,
        formerDate: materialData.FormerDate || null,
        versionOrRevisionNumber: materialData.versionOrRevisionNumber || '',
        storageTemperature: materialData.StorageTemperature || '',
        comments: materialData.Comments || '',
        availabilityStatus: materialData.AvailabilityStatus || '',
    };

    const res = yield axiosConfig.put(`/materials-module/${materialId}`, formattedPayload);
    const response = get(res, ['data', 'data'], {});

    notification.destroy();
    if (res.status === 200) {
      yield fetchMaterials();
      notification.success({
        message: 'Material Updated Successfully.',
      });
      yield put(updateMaterialSuccess({ response }));
    } else {
      yield put(updateMaterialFailure());
      notification.error({
        message: 'Something went wrong.',
      });
    }
  } catch (error) {
    yield put(updateMaterialFailure());
    notification.destroy();
    notification.error({
      message: 'A problem occurred while updating the Material.',
      description: get(error, ['response', 'data', 'message'], error.message),
    });
  }
}

/** Function to Update material with file upload */
export const OnUpdateMaterialData = async (payload) => {
  try {
    const materialData = payload.data;
    const materialId = payload.id;
    let fileKey = null;
    let fileName = null;
    let fileType = null;
    
    // Check if new file is being uploaded (file is passed as dataSheet array)
    if (materialData.dataSheet && materialData.dataSheet.length > 0) {
      const file = materialData.dataSheet[0];
      
      // Get presigned URL from backend
      const resFile = await axiosConfig.get(
        `/materials-module/get-pre-signed-url?fileName=${file.name}&contentType=${file.type}`
      );
      const responseFile = get(resFile, ['data', 'data'], {});

      if (responseFile && responseFile.presignedUrl) {
        // Upload file directly to S3 using presigned URL
        const presignedResponse = await axios.put(
          responseFile.presignedUrl,
          file
        );

        if (presignedResponse.status === 200) {
          // Store fileKey, fileName, and fileType from S3 response
          fileKey = responseFile.fileKey;
          fileName = responseFile.fileName || file.name;
          fileType = file.type || null;
        } else {
          notification.error({
            message: 'Something went wrong.',
          });
          return false;
        }
      } else {
        notification.error({ message: 'File Uploading Failed. Please try again.' });
        return false;
      }
    }

    // Format the payload according to API requirements with materialsModule wrapper
    const formattedPayload = {
        materialsModuleId: materialId,
        materialCategory: materialData.MaterialCategory || '',
        name: materialData.Name || '',
        productCode: materialData.ProductCode || '',
        datasheet: materialData.Datasheet || '',
        manufactureName: materialData.ManufactureName || '',
        tradeName: materialData.TradeName || '',
        applications: materialData.Applications || '',
        reference: materialData.Reference || '',
        revisionDate: materialData.RevisionDate || null,
        formerDate: materialData.FormerDate || null,
        versionOrRevisionNumber: materialData.versionOrRevisionNumber || '',
        storageTemperature: materialData.StorageTemperature || '',
        comments: materialData.Comments || '',
        availabilityStatus: materialData.AvailabilityStatus || '',
        ...(fileKey && { fileKey: fileKey }),
        ...(fileName && { fileName: fileName }),
        ...(fileType && { fileType: fileType }),
    };

    const res = await axiosConfig.put(`/materials-module/${materialId}`, formattedPayload);
    const response = get(res, ['data', 'data'], {});

    notification.destroy();
    if (res.status === 200) {
      notification.success({
        message: 'Material Updated Successfully.',
      });
      return false; // Return false to indicate success (following calibration pattern)
    } else {
      notification.error({
        message: 'Something went wrong.',
      });
      return false;
    }
  } catch (error) {
    console.log(error);
    notification.destroy();
    notification.error({
      message: 'A problem occurred while updating the Material.',
      description: get(error, ['response', 'data', 'message'], error.message || error),
    });
    return false;
  }
};

/** Function to Bulk Upload materials */
function* bulkUploadMaterials(payload) {
  try {
    const materialsArray = payload.data; // Array of materials to upload

    const res = yield axiosConfig.post(`/materials-module/bulk-create`, materialsArray);
    const response = get(res, ['data', 'data'], {});

    notification.destroy();
    if (res.status === 200 || res.status === 201) {
      yield fetchMaterials();
      notification.success({
        message: 'Materials Bulk Uploaded Successfully.',
      });
      yield put(bulkUploadMaterialsSuccess({ response }));
    } else {
      yield put(bulkUploadMaterialsFailure());
      notification.error({
        message: 'Something went wrong.',
      });
    }
  } catch (error) {
    yield put(bulkUploadMaterialsFailure());
    notification.destroy();
    notification.error({
      message: 'A problem occurred while bulk uploading Materials.',
      description: get(error, ['response', 'data', 'message'], error.message),
    });
  }
}

/** Function to Share material certificate */
function* shareMaterial(payload) {
  try {
    const materialId = payload.materialId;
    const certificateId = payload.certificateId;
    const emailAddressArray = payload.emailAddress || [];
    
    // Build query string with email addresses
    let emailURL = '';
    emailAddressArray.forEach((email) => {
      const trimmedEmail = email.trim();
      if (trimmedEmail) {
        emailURL += `&emailToDestinations=${encodeURIComponent(trimmedEmail)}`;
      }
    });

    // Include certificateId in the query string
    const certificateIdParam = certificateId ? `&certificateId=${certificateId}` : '';

    const res = yield axiosConfig.get(
      `/materials-module/share-certificate?materialId=${materialId}${certificateIdParam}${emailURL}`
    );
    const response = get(res, ['data', 'data'], {});

    notification.destroy();
    if (res.status === 200) {
      notification.success({
        message: 'Certificate Shared Successfully.',
        duration: 3,
      });
      yield put(shareMaterialSuccess({ response }));
    } else {
      yield put(shareMaterialFailure());
      notification.error({
        message: 'Something went wrong. Please try again.',
      });
    }
  } catch (error) {
    yield put(shareMaterialFailure());
    notification.destroy();
    notification.error({
      message: 'A problem occurred while sharing the certificate.',
      description: get(error, ['response', 'data', 'message'], error.message || error),
    });
  }
}

export default function* materialsSaga() {
  yield takeLatest(FETCH_MATERIALS, fetchMaterials);
  yield takeLatest(ADD_MATERIAL, addMaterial);
  yield takeLatest(UPDATE_MATERIAL, updateMaterial);
  yield takeLatest(BULK_UPLOAD_MATERIALS, bulkUploadMaterials);
  yield takeLatest(SHARE_MATERIAL, shareMaterial);
}

